import React, { useState, useEffect } from 'react';
import { Popover, Typography, Space, Button, Divider, Tag, Spin } from 'antd';
import { EnvironmentOutlined, PhoneOutlined, MailOutlined, TeamOutlined, ShopOutlined, LinkOutlined, UserOutlined } from '@ant-design/icons';
import { EstablishmentLocation, Contact } from '../../../types';
import { useAppDispatch } from '../../../utils/hooks/redux';
import { fetchSingleEstablishmentLocation } from '../../../redux/features/establishmentLocation/establishmentLocationAsyncThunk';
import CopyToClipboard from '../../../components/CopyToClipboard';

const { Text, Title } = Typography;

interface EstablishmentPopoverProps {
    children: React.ReactNode;
    establishment: EstablishmentLocation | null;
    onManage?: () => void;
}

const EstablishmentPopover: React.FC<EstablishmentPopoverProps> = ({ children, establishment, onManage }) => {
    const dispatch = useAppDispatch();
    const [fullEstablishment, setFullEstablishment] = useState<EstablishmentLocation | null>(null);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        // Only fetch if popover is opened AND we don't have data yet
        if (isOpen && establishment?.id && !fullEstablishment) {
            loadFullDetails();
        }
    }, [isOpen, establishment?.id]);

    // Reset cache when establishment changes (prevents showing stale data)
    useEffect(() => {
        setFullEstablishment(null);
    }, [establishment?.id]);

    const loadFullDetails = async () => {
        if (!establishment?.id) return;

        try {
            setLoading(true);
            const result = await dispatch(fetchSingleEstablishmentLocation(establishment.id)).unwrap();
            setFullEstablishment(result);
        } catch (error) {
            console.error("Failed to fetch establishment details:", error);
        } finally {
            setLoading(false);
        }
    };

    if (!establishment) {
        return <>{children}</>;
    }

    const displayEstablishment = fullEstablishment || establishment;

    // Get the primary contact or first contact
    const contacts = displayEstablishment.contacts || [];
    const primaryContact = contacts.find(c => c.isPrimary) || contacts[0];
    const formLink = primaryContact?.lostAndFoundFormLink;

    const content = loading ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
            <Spin />
        </div>
    ) : (
        <div style={{ maxWidth: 380 }}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
                {/* Header */}
                <div>
                    <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
                        <CopyToClipboard text={displayEstablishment.name} size="small">
                            {displayEstablishment.name}
                        </CopyToClipboard>
                    </Title>
                    {displayEstablishment.code && (
                        <CopyToClipboard text={displayEstablishment.code} size="small">
                            <Tag color="blue">{displayEstablishment.code}</Tag>
                        </CopyToClipboard>
                    )}
                    {displayEstablishment.specificType && (
                        <Tag color="purple">{displayEstablishment.specificType}</Tag>
                    )}
                </div>

                <Divider style={{ margin: '8px 0' }} />

                {/* Establishment Contact Information */}
                {(displayEstablishment.address || displayEstablishment.phone || displayEstablishment.email) ? (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {displayEstablishment.address && (
                            <div style={{ display: 'flex', gap: 8 }}>
                                <EnvironmentOutlined style={{ color: '#1890ff', marginTop: 2 }} />
                                <CopyToClipboard
                                    text={[
                                        displayEstablishment.address,
                                        [displayEstablishment.city, displayEstablishment.state, displayEstablishment.zipCode].filter(Boolean).join(', '),
                                        displayEstablishment.country
                                    ].filter(Boolean).join('\n')}
                                    size="small"
                                >
                                    <Text style={{ flex: 1, fontSize: 12 }}>
                                        {displayEstablishment.address}
                                        {displayEstablishment.city && `, ${displayEstablishment.city}`}
                                        {displayEstablishment.state && `, ${displayEstablishment.state}`}
                                        {displayEstablishment.zipCode && ` ${displayEstablishment.zipCode}`}
                                        {displayEstablishment.country && ` - ${displayEstablishment.country}`}
                                    </Text>
                                </CopyToClipboard>
                            </div>
                        )}

                        {displayEstablishment.phone && (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <PhoneOutlined style={{ color: '#1890ff' }} />
                                <CopyToClipboard text={displayEstablishment.phone} size="small">
                                    <a href={`tel:${displayEstablishment.phone}`} style={{ fontSize: 12, color: '#2563eb' }}>
                                        {displayEstablishment.phone}
                                    </a>
                                </CopyToClipboard>
                            </div>
                        )}

                        {displayEstablishment.email && (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <MailOutlined style={{ color: '#1890ff' }} />
                                <CopyToClipboard text={displayEstablishment.email} size="small">
                                    <a href={`mailto:${displayEstablishment.email}`} style={{ fontSize: 12, color: '#2563eb' }}>
                                        {displayEstablishment.email}
                                    </a>
                                </CopyToClipboard>
                            </div>
                        )}
                    </Space>
                ) : (
                    <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                        No establishment contact details
                    </Text>
                )}

                {/* Primary Contact Section */}
                {primaryContact && (
                    <>
                        <Divider style={{ margin: '8px 0' }} />
                        <div>
                            <div style={{ fontWeight: 600, color: '#4b5563', marginBottom: 8, fontSize: '12px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <UserOutlined />
                                PRIMARY CONTACT
                                {primaryContact.department && (
                                    <Tag color="blue" style={{ marginLeft: 8, fontSize: 10 }}>{primaryContact.department}</Tag>
                                )}
                            </div>
                            <Space direction="vertical" size={4} style={{ width: '100%', paddingLeft: 8 }}>
                                {primaryContact.name && (
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <span style={{ color: '#6b7280', fontSize: 12 }}>Name:</span>
                                        <CopyToClipboard text={primaryContact.name} size="small">
                                            <Text style={{ fontSize: 12, fontWeight: 500 }}>{primaryContact.name}</Text>
                                        </CopyToClipboard>
                                    </div>
                                )}
                                {primaryContact.email && (
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <MailOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                                        <CopyToClipboard text={primaryContact.email} size="small">
                                            <a href={`mailto:${primaryContact.email}`} style={{ fontSize: 12, color: '#2563eb' }}>
                                                {primaryContact.email}
                                            </a>
                                        </CopyToClipboard>
                                    </div>
                                )}
                                {primaryContact.phone && (
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <PhoneOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                                        <CopyToClipboard text={primaryContact.phone} size="small">
                                            <a href={`tel:${primaryContact.phone}`} style={{ fontSize: 12, color: '#2563eb' }}>
                                                {primaryContact.phone}
                                            </a>
                                        </CopyToClipboard>
                                    </div>
                                )}
                            </Space>
                        </div>
                    </>
                )}

                {/* L&F Form Link */}
                {formLink && (
                    <>
                        <Divider style={{ margin: '8px 0' }} />
                        <div>
                            <div style={{ fontWeight: 600, color: '#4b5563', marginBottom: 8, fontSize: '12px' }}>
                                LOST & FOUND FORM
                            </div>
                            <div style={{ paddingLeft: 8 }}>
                                <CopyToClipboard text={formLink} size="small">
                                    <a
                                        href={formLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            color: '#2563eb',
                                            fontSize: '12px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            wordBreak: 'break-all'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <LinkOutlined style={{ flexShrink: 0 }} />
                                        {formLink.length > 45 ? `${formLink.substring(0, 45)}...` : formLink}
                                    </a>
                                </CopyToClipboard>
                            </div>
                        </div>
                    </>
                )}

                {/* Places and Contacts Count */}
                {(displayEstablishment.places !== undefined || displayEstablishment.contacts !== undefined) && (
                    <>
                        <Divider style={{ margin: '8px 0' }} />
                        <Space size="middle">
                            {displayEstablishment.places !== undefined && (
                                <Tag icon={<ShopOutlined />} color={displayEstablishment.places.length > 0 ? "blue" : "default"}>
                                    {displayEstablishment.places.length} Place{displayEstablishment.places.length !== 1 ? 's' : ''}
                                </Tag>
                            )}
                            {displayEstablishment.contacts !== undefined && (
                                <Tag icon={<TeamOutlined />} color={displayEstablishment.contacts.length > 0 ? "green" : "default"}>
                                    {displayEstablishment.contacts.length} Contact{displayEstablishment.contacts.length !== 1 ? 's' : ''}
                                </Tag>
                            )}
                        </Space>
                    </>
                )}

                {/* Action Button */}
                {onManage && (
                    <>
                        <Divider style={{ margin: '8px 0' }} />
                        <Button
                            type="primary"
                            size="small"
                            block
                            onClick={onManage}
                        >
                            Manage Details & Contacts
                        </Button>
                    </>
                )}
            </Space>
        </div>
    );

    return (
        <Popover
            content={content}
            title={null}
            trigger="hover"
            placement="top"
            overlayStyle={{ maxWidth: 420 }}
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            {children}
        </Popover>
    );
};

export default EstablishmentPopover;
